import { type MutableRefObject, useCallback } from "react";
import { sendAnalyticsNonInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { AnalyticsEventCategory } from "src/analytics/analyticsEventTypes";
import {
  useIsInViewport,
  type HandleInViewportChange,
} from "./useIsInViewport";

type Args = {
  ref: MutableRefObject<HTMLElement | null>;
  category: AnalyticsEventCategory;
  action: string;
  label?: string;
  baseOffsetForScrollIn?: number;
  threshold?: number;
  isReady?: boolean;
};
/**
 * This hook is used to send a non-interaction analytics event when an
 * element enters the viewport for the first time, and only once.
 * It must be `isReady` before it will send the event, and utilises the
 * `useIsInViewport` hook to disconnect the observer once the event has been sent.
 *
 * @param ref The element to observe.
 * @param category The category of the event.
 * @param action The action of the event.
 * @param label The label of the event.
 * @param baseOffsetForScrollIn A value for margin bottom to offset the base root margin.
 * @param threshold The minimum amount of the element that must be visible in the viewport, from 0-1.
 * @param isReady If true, the event will be eligible to be sent.
 */
export function useAnalyticsEventHasEnteredViewport({
  ref,
  category,
  action,
  label,
  baseOffsetForScrollIn = 0,
  threshold = 0.15,
  isReady = true,
}: Args) {
  const handleInViewportChange: HandleInViewportChange = useCallback(
    (isInViewport: boolean) => {
      if (isInViewport && isReady) {
        sendAnalyticsNonInteractionEvent({ category, action, label });
        return "unobserve";
      }

      return "observe";
    },
    [action, category, label, isReady],
  );

  useIsInViewport({
    ref,
    handleInViewportChange,
    threshold,
    baseOffset: baseOffsetForScrollIn,
  });
}
